import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Button } from '@airia/ui';
import { Textarea } from '../../ui/textarea';
import { Slider } from '../../ui/slider';
import { Send, ChevronDown, ChevronUp } from 'lucide-react';
import { LoadingSpinner } from '../../ui/loading-spinner';
import DraftPreview from './DraftPreview';
import { formatEmailBody } from '../../lib/formatEmailBody';
import { parseEmailTags } from '../../lib/emailParser';
import { logger } from '../../lib/logger';
const STORAGE_KEY = 'airia-reply-preferences';
const ReadModeReplyForm = ({ onGenerate }) => {
    const [messageIdea, setMessageIdea] = useState('');
    const [wordCount, setWordCount] = useState(100);
    const [tone, setTone] = useState('neutral');
    const [style, setStyle] = useState('formal');
    const [showOptions, setShowOptions] = useState(false);
    // Response state
    const [isGenerating, setIsGenerating] = useState(false);
    const [aiResponse, setAiResponse] = useState(null);
    const [error, setError] = useState(null);
    // Load saved preferences on mount
    useEffect(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const prefs = JSON.parse(saved);
                setWordCount(prefs.wordCount || 100);
                setTone(prefs.tone || 'neutral');
                setStyle(prefs.style || 'formal');
            }
        }
        catch (error) {
            console.error('Failed to load reply preferences:', error);
        }
    }, []);
    const savePreferences = () => {
        const prefs = {
            wordCount,
            tone,
            style,
        };
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
        }
        catch (error) {
            console.error('Failed to save reply preferences:', error);
        }
    };
    const buildReplyPrompt = () => {
        let prompt = 'Draft a reply to this email';
        if (messageIdea.trim()) {
            prompt += ` that ${messageIdea.trim()}`;
        }
        if (wordCount < 200) {
            prompt += `. Keep it around ${wordCount} words`;
        }
        else {
            prompt += `. Make it as long as needed`;
        }
        prompt += `. Use a ${tone} tone`;
        prompt += ` and ${style} communication style`;
        return prompt;
    };
    const handleGenerate = () => {
        if (!messageIdea.trim())
            return;
        savePreferences();
        setIsGenerating(true);
        setAiResponse(null);
        setError(null);
        const prompt = buildReplyPrompt();
        onGenerate(prompt, (response) => {
            setIsGenerating(false);
            setAiResponse(response);
        });
    };
    const handleRegenerate = () => {
        handleGenerate();
    };
    const handleOpenReply = async (editedContent) => {
        setError(null);
        try {
            if (typeof Office !== 'undefined' &&
                Office.context.mailbox &&
                Office.context.mailbox.item) {
                const item = Office.context.mailbox.item;
                if (item.displayReplyAllForm) {
                    item.displayReplyAllForm({
                        htmlBody: formatEmailBody(editedContent),
                    });
                }
                else {
                    logger.error('displayReplyAllForm not available');
                    setError('Reply function not available');
                }
            }
            else {
                logger.error('Office context or mailbox item not available');
                setError('Office context not available');
            }
        }
        catch (err) {
            logger.error('Error creating reply:', err);
            setError('Failed to open reply');
        }
    };
    // Extract plain text content from AI response
    const getResponseContent = () => {
        if (!aiResponse)
            return '';
        // Check for reply email tags
        const parts = parseEmailTags(aiResponse);
        const replyPart = parts.find((p) => p.type === 'reply' && p.replyEmailData);
        if (replyPart && replyPart.replyEmailData) {
            return replyPart.replyEmailData.body;
        }
        // Fall back to raw response
        return aiResponse;
    };
    const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);
    // If we have a response, show the preview
    if (aiResponse) {
        return (_jsxs("div", { className: "my-4", children: [_jsx(DraftPreview, { content: getResponseContent(), onRegenerate: handleRegenerate, onOpenInOutlook: handleOpenReply, isLoading: isGenerating, variant: "reply", error: error }), _jsx(Button, { variant: "ghost", size: "sm", onClick: () => setAiResponse(null), className: "w-full mt-2 text-muted-foreground", children: "Start Over" })] }));
    }
    return (_jsxs("div", { className: "my-4 space-y-4", children: [_jsxs("div", { className: "space-y-2", children: [_jsx("label", { className: "text-sm font-medium text-foreground", children: "What would you like to say?" }), _jsx(Textarea, { placeholder: "e.g., Thank them for the update and confirm I'll review by Friday", value: messageIdea, onChange: (e) => setMessageIdea(e.target.value), className: "min-h-[80px] resize-none", disabled: isGenerating })] }), _jsxs("div", { className: "border border-border rounded-lg", children: [_jsxs("button", { type: "button", onClick: () => setShowOptions(!showOptions), className: "w-full flex items-center justify-between p-3 text-sm font-medium text-foreground hover:bg-accent/50 rounded-lg transition-colors", children: [_jsx("span", { children: "Options" }), showOptions ? (_jsx(ChevronUp, { className: "size-4 text-muted-foreground" })) : (_jsx(ChevronDown, { className: "size-4 text-muted-foreground" }))] }), showOptions && (_jsxs("div", { className: "p-3 pt-0 space-y-4", children: [_jsxs("div", { className: "space-y-2", children: [_jsx("label", { className: "text-sm font-medium text-foreground", children: "Email Length" }), _jsx(Slider, { value: [wordCount], onValueChange: ([value]) => setWordCount(value), min: 30, max: 200, step: 10, className: "w-full" }), _jsx("div", { className: "text-xs text-muted-foreground", children: wordCount === 200 ? '200+ words' : `${wordCount} words` })] }), _jsxs("div", { className: "space-y-2", children: [_jsx("label", { className: "text-sm font-medium text-foreground", children: "Tone" }), _jsx("div", { className: "flex gap-2", children: ['positive', 'neutral', 'negative'].map((t) => (_jsx(Button, { variant: tone === t ? 'default' : 'outline', size: "sm", onClick: () => setTone(t), className: "flex-1", disabled: isGenerating, children: capitalize(t) }, t))) })] }), _jsxs("div", { className: "space-y-2", children: [_jsx("label", { className: "text-sm font-medium text-foreground", children: "Style" }), _jsx("div", { className: "flex gap-2", children: ['formal', 'informal', 'factual'].map((s) => (_jsx(Button, { variant: style === s ? 'default' : 'outline', size: "sm", onClick: () => setStyle(s), className: "flex-1", disabled: isGenerating, children: capitalize(s) }, s))) })] })] }))] }), _jsx(Button, { onClick: handleGenerate, className: "w-full", disabled: isGenerating || !messageIdea.trim(), children: isGenerating ? (_jsxs("span", { className: "flex items-center gap-2", children: [_jsx(LoadingSpinner, {}), "Generating..."] })) : (_jsxs(_Fragment, { children: [_jsx(Send, { className: "size-4 mr-2" }), "Generate Reply Draft"] })) }), error && (_jsx("div", { className: "p-2 bg-destructive/10 border border-destructive/20 rounded text-sm text-destructive", children: error }))] }));
};
export default ReadModeReplyForm;
